<template>
  <w-multiple-tree-selection-filter
    ref="multipleTreeSelection"
    :value="value"
    :items="categoryItems"
    :items-pending="categories_pending"
    :label="label"
    item-text="data.label"
    item-children="data.children"
    item-value="id"
    :disabled="disabled"
    :filter-options="hasFilterOption ? filterOptions : []"
    :filter-option-value="filterOptionValue"
    :option-search-key="categoryOptionSearchKey"
    :option-select-disabled="!moreThanOneAttributesSelected"
    :select-parents="selectParents"
    :sort-items="false"
    @input="$emit('input', $event)"
  />
</template>

<script>
/** MIXINS **/
import filterHierarchyStructureMixin from '@/mixins/filter-hierarchy-structure-mixin';
import dynamicData from '@/mixins/api_data/dynamic-data-mixin';
// components
import WorkAutocomplete from '@/components/global_components/forms/WorkAutocomplete';
import i18n from '@/language/i18n';
import { sortItemsModule } from '@/mixins/component/sorted-items-mixin';

/**
 * @component WorkCategoryTreeFilter
 * @description Category tree filter based on `WorkAutocomplete`.
 * Loads categories from the API and supports parent node selection
 * and additional categories. Inherits all `WorkAutocomplete` props.
 *
 * @prop {String} [filterName='categories'] - Filter identifier for the hierarchy structure.
 * @prop {String} [path='productcategories/treehierarchy'] - API path for the category tree.
 * @prop {String} [categoryOptionSearchKey='categoryRelation'] - Search key for category filter options.
 * @prop {Object} [categoryTableFilters] - Additional table filters for categories.
 * @prop {Boolean} [hasFilterOption=true] - Show filter option buttons.
 * @prop {Boolean} [selectParents=false] - Make parent nodes selectable.
 * @prop {String} [label] - Field label (default: translation for 'Categories').
 * @prop {Array} [additionalCategories=[]] - Additional categories added to the list.
 *
 * @emits {Object|Array} input - Emitted when a category is selected.
 */
export default {
  name: 'WorkCategoryTreeFilter',
  props: {
    ...WorkAutocomplete.props,
    filterName: { type: String, default: 'categories' },
    path: { type: String, default: 'productcategories/treehierarchy' },
    categoryOptionSearchKey: { type: String, default: 'categoryRelation' },
    categoryTableFilters: { type: Object, default: undefined },
    hasFilterOption: { type: Boolean, default: true },
    selectParents: { type: Boolean, default: false },
    label: { type: String, default: i18n.t('Common.categories') },
    additionalCategories: { type: Array, default: () => [] },
  },
  mixins: [
    dynamicData(
      'categories',
      (self) => self.path,
      (response) => response.data.data,
    ),
    filterHierarchyStructureMixin(
      (self) => self.filterName,
      (self) => self.categoryOptionSearchKey,
      undefined,
      (self) => self.categoryTableFilters ?? undefined
    ),
  ],
  computed: {
    sortedItems() {
      return sortItemsModule.getSortedItems(
        this.$props,
        this.categories?.children ?? []
      );
    },
    categoryItems() {
      let categories = [
        ...(this.additionalCategories ?? []),
        ...(this.sortedItems ?? []),
      ];
      return categories;
    },
  },
};
</script>
